import type { ReleaseNote } from '@clockwise/clients/release-notes';
import { ApiError, isValidIsoDate } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { releaseNotesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Add / edit a release note. Visible to every signed-in user the moment it's
 * saved (no draft/publish step, unlike announcements) — see
 * docs/release-notes.md. Delete/restore live in the card list, not here, same
 * split as AnnouncementsPanel.
 */
const ReleaseNoteFormSchema = z.object({
  releaseName: z.string().trim().min(1, 'Required').max(200, 'Up to 200 characters'),
  releaseVersion: z.string().trim().min(1, 'Required').max(50, 'Up to 50 characters'),
  releasedAt: z.string().min(1, 'Pick a date').refine(isValidIsoDate, 'Pick a valid date'),
  details: z.string().max(20000, 'Up to 20,000 characters'),
});
type ReleaseNoteFormValues = z.infer<typeof ReleaseNoteFormSchema>;

const blankValues: ReleaseNoteFormValues = {
  releaseName: '',
  releaseVersion: '',
  releasedAt: '',
  details: '',
};

interface ReleaseNoteFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row being edited, or `null` to add a new release note. */
  releaseNote: ReleaseNote | null;
}

export function ReleaseNoteFormSheet({
  open,
  onOpenChange,
  releaseNote,
}: ReleaseNoteFormSheetProps) {
  const isEdit = releaseNote !== null;
  const queryClient = useQueryClient();

  const form = useForm<ReleaseNoteFormValues>({
    resolver: zodResolver(ReleaseNoteFormSchema),
    defaultValues: blankValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      releaseNote
        ? {
            releaseName: releaseNote.releaseName,
            releaseVersion: releaseNote.releaseVersion,
            releasedAt: releaseNote.releasedAt,
            details: releaseNote.details ?? '',
          }
        : blankValues,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, releaseNote?.id]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.releaseNotes.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const saveMutation = useMutation({
    mutationFn: (values: ReleaseNoteFormValues) => {
      const body = {
        releaseName: values.releaseName.trim(),
        releaseVersion: values.releaseVersion.trim(),
        releasedAt: values.releasedAt,
        details: values.details.trim() || null,
      };
      return isEdit && releaseNote
        ? releaseNotesApi.patch(releaseNote.id, body)
        : releaseNotesApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Release note updated' : 'Release note published');
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => applyFieldErrors(form, err),
  });

  const busy = saveMutation.isPending;
  const onSubmit = form.handleSubmit((v) => saveMutation.mutate(v));

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: busy,
    onOpenChange,
  });

  const errors = form.formState.errors;

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit release note' : 'Add release note'}</SheetTitle>
            <SheetDescription>
              Visible to everyone on the What&apos;s New tab as soon as you save.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="release-note-name">Release name</Label>
                  <Input
                    id="release-note-name"
                    placeholder="Autumn Update"
                    disabled={busy}
                    aria-invalid={Boolean(errors.releaseName)}
                    {...form.register('releaseName')}
                  />
                  {errors.releaseName ? (
                    <p className="text-xs text-destructive">{errors.releaseName.message}</p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="release-note-version">Version</Label>
                  <Input
                    id="release-note-version"
                    placeholder="v2.4.0"
                    disabled={busy}
                    aria-invalid={Boolean(errors.releaseVersion)}
                    {...form.register('releaseVersion')}
                  />
                  {errors.releaseVersion ? (
                    <p className="text-xs text-destructive">{errors.releaseVersion.message}</p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="release-note-date">Released</Label>
                <DatePicker
                  id="release-note-date"
                  value={form.watch('releasedAt')}
                  onChange={(v) =>
                    form.setValue('releasedAt', v, { shouldValidate: true, shouldDirty: true })
                  }
                  disabled={busy}
                  ariaInvalid={Boolean(errors.releasedAt)}
                />
                {errors.releasedAt ? (
                  <p className="text-xs text-destructive">{errors.releasedAt.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="release-note-details">Details</Label>
                <Textarea
                  id="release-note-details"
                  rows={8}
                  placeholder="Optional — markdown shown on the release note's card"
                  disabled={busy}
                  aria-invalid={Boolean(errors.details)}
                  {...form.register('details')}
                />
                {errors.details ? (
                  <p className="text-xs text-destructive">{errors.details.message}</p>
                ) : null}
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Publish'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

/** Route the server's field errors to the matching inputs (G12). */
function applyFieldErrors(form: UseFormReturn<ReleaseNoteFormValues>, err: unknown) {
  const fields = err instanceof ApiError ? err.fields : undefined;
  let matched = false;
  for (const f of fields ?? []) {
    if (
      f.path === 'releaseName' ||
      f.path === 'releaseVersion' ||
      f.path === 'releasedAt' ||
      f.path === 'details'
    ) {
      form.setError(f.path, { message: f.message });
      matched = true;
    }
  }
  if (!matched) {
    toast.error(err instanceof ApiError ? err.message : 'Could not save this release note');
  }
}
